import {ValueObject} from '@s-ui/domain'
import {toQueryString} from '@s-ui/js/lib/string'

import {FiltersSearchValueObject} from './FiltersSearchValueObject.js'
import {PaginationSearchValueObject} from './PaginationSearchValueObject.js'
import {SortSearchValueObject} from './SortSearchValueObject.js'

export class RequestSearchValueObject extends ValueObject {
  #filters
  #pagination
  #sort

  constructor({filters, pagination, sort}) {
    super()
    this.#filters = filters
    this.#pagination = pagination
    this.#sort = sort
  }

  static create({filters, pagination, sort}) {
    return new RequestSearchValueObject({
      filters: FiltersSearchValueObject.create(filters),
      pagination: PaginationSearchValueObject.create(pagination),
      sort: SortSearchValueObject.create(sort)
    })
  }

  static createToApi({config, listingSectionId, search}) {
    const filters = FiltersSearchValueObject.createFromFilters({config, filters: search, listingSectionId})
    const pagination = PaginationSearchValueObject.createFromFilters({config, page: search.page, size: search.size})
    const sort = SortSearchValueObject.create({sortBy: search.sortBy, sortOrder: search.sortOrder})

    return new RequestSearchValueObject({filters, pagination, sort})
  }

  static createFromApi({config, listingSectionId, search, url}) {
    const {LISTING_SECTION_IDS} = config.get('COMMON')
    const filters = FiltersSearchValueObject.createFromApi({config, filters: search.filters, listingSectionId, url})
    const pagination = PaginationSearchValueObject.createWithPaginationMap({config, pagination: search.pagination})
    const sort =
      listingSectionId === LISTING_SECTION_IDS.RENTING_VEHICLES
        ? SortSearchValueObject.createForRentingWithSortMapFromUrl({config, sort: search.sort, url})
        : SortSearchValueObject.createWithSortMap({config, sort: search.sort})

    return new RequestSearchValueObject({filters, pagination, sort})
  }

  static createFromAIApi({config, listingSectionId, search}) {
    const {LISTING_SECTION_IDS} = config.get('COMMON')
    return RequestSearchValueObject.createFromApi({
      config,
      listingSectionId: listingSectionId ?? LISTING_SECTION_IDS.ALL_VEHICLES,
      search
    })
  }

  static createQueryParams({config, listingSectionId, search}) {
    const filters = FiltersSearchValueObject.createQueryParams({config, filters: search, listingSectionId})
    const pagination = PaginationSearchValueObject.createQueryParams({config, page: search.page})
    const sort = SortSearchValueObject.createQueryParams({config, sortBy: search.sortBy, sortOrder: search.sortOrder})

    return new RequestSearchValueObject({filters, pagination, sort})
  }

  getSearchFromFilters() {
    return {
      filters: this.#filters.getSearchFromFilters(),
      pagination: {page: this.#pagination.page, size: this.#pagination.size},
      sort: {order: this.#sort.sortOrder, term: this.#sort.sortBy}
    }
  }

  getSearchForStockAggregations() {
    return this.#filters.getSearchForStockAggregations()
  }

  get searchForFilters() {
    return {...this.#filters.getSearchForFilters(), ...this.#sort.value, page: this.#pagination.page}
  }

  get hasQueryParams() {
    return this.#filters.hasQueryParams || this.#pagination.hasQueryParams || this.#sort.hasQueryParams
  }

  get queryString() {
    return toQueryString(
      {...this.#filters.queryParams, ...this.#pagination.queryParams, ...this.#sort.queryParams},
      {addQueryPrefix: true, skipNulls: true}
    )
  }

  get queryParams() {
    return {...this.#filters.queryParams, ...this.#pagination.queryStrings, ...this.#sort.queryStrings}
  }

  get value() {
    return {filters: this.#filters.value, pagination: this.#pagination.value, sort: this.#sort.value}
  }

  toJSON() {
    return {filters: this.#filters.toJSON(), pagination: this.#pagination.toJSON(), sort: this.#sort.toJSON()}
  }
}
